import type { PlaybookStep, PlaybookRunStep } from "./types";

/* Critical-path scheduling — client mirror of api/src/playbooks/schedule.ts.
 * Used to render the Gantt (bars, critical path, slack) and to recompute a
 * live preview in the designer. Byte-compatible with the API. */

const DAY = 86_400_000;
export const addDays = (isoDate: string, n: number): string =>
  new Date(Date.parse(`${isoDate}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);

export interface Scheduled {
  id: string; durationDays: number; dependsOn: string[];
  earliestStart: number; earliestFinish: number;
  latestStart: number; latestFinish: number;
  slack: number; critical: boolean;
}
interface Node { id: string; duration: number; offset: number; deps: string[]; }

function topoOrder(nodes: Node[]): Node[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const state = new Map<string, 0 | 1 | 2>();
  const out: Node[] = [];
  const visit = (n: Node) => {
    const s = state.get(n.id) ?? 0;
    if (s === 2) return;
    if (s === 1) throw new Error(`Dependency cycle at step ${n.id}`);
    state.set(n.id, 1);
    for (const d of n.deps) { const dn = byId.get(d); if (dn) visit(dn); }
    state.set(n.id, 2);
    out.push(n);
  };
  for (const n of nodes) visit(n);
  return out;
}

export function criticalPath(input: { id: string; durationDays?: number; offsetDays?: number; dependsOn?: string[] }[]): Scheduled[] {
  const nodes: Node[] = input.map((s) => ({
    id: s.id, duration: Math.max(0, Math.round(s.durationDays ?? 1)),
    offset: Math.max(0, Math.round(s.offsetDays ?? 0)), deps: (s.dependsOn ?? []).filter((d) => input.some((x) => x.id === d)),
  }));
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const ordered = topoOrder(nodes);

  const es = new Map<string, number>(), ef = new Map<string, number>();
  for (const n of ordered) {
    const start = n.deps.length ? Math.max(...n.deps.map((d) => ef.get(d) ?? 0)) + n.offset : n.offset;
    es.set(n.id, start); ef.set(n.id, start + n.duration);
  }
  const projectFinish = Math.max(0, ...nodes.map((n) => ef.get(n.id) ?? 0));

  const lf = new Map<string, number>(), ls = new Map<string, number>();
  const successors = new Map<string, string[]>();
  for (const n of nodes) for (const d of n.deps) (successors.get(d) ?? successors.set(d, []).get(d)!).push(n.id);
  for (const n of [...ordered].reverse()) {
    const succ = successors.get(n.id) ?? [];
    const finish = succ.length ? Math.min(...succ.map((s) => (ls.get(s) ?? projectFinish) - (byId.get(s)?.offset ?? 0))) : projectFinish;
    lf.set(n.id, finish); ls.set(n.id, finish - n.duration);
  }

  return nodes.map((n) => {
    const earliestStart = es.get(n.id) ?? 0, earliestFinish = ef.get(n.id) ?? 0;
    const latestStart = ls.get(n.id) ?? earliestStart, latestFinish = lf.get(n.id) ?? earliestFinish;
    const slack = Math.max(0, latestStart - earliestStart);
    return { id: n.id, durationDays: n.duration, dependsOn: n.deps, earliestStart, earliestFinish, latestStart, latestFinish, slack, critical: slack === 0 };
  });
}

export function planDuration(steps: { id: string; durationDays?: number; offsetDays?: number; dependsOn?: string[] }[]): number {
  return Math.max(0, ...criticalPath(steps).map((s) => s.earliestFinish));
}

export function scheduleRunSteps(
  templateSteps: PlaybookStep[], planStartDate: string,
  mkId: (templateStepId: string) => string, actionable: (kind: string) => boolean,
): Omit<PlaybookRunStep, "taskId">[] {
  const idMap = new Map(templateSteps.map((s) => [s.id, mkId(s.id)]));
  const cpm = new Map(criticalPath(templateSteps).map((c) => [c.id, c]));
  return templateSteps.map((s) => {
    const c = cpm.get(s.id)!;
    return {
      id: idMap.get(s.id)!, templateStepId: s.id, label: s.label, kind: s.kind,
      status: actionable(s.kind) ? "pending" : "skipped",
      dependsOn: (s.dependsOn ?? []).map((d) => idMap.get(d)).filter((x): x is string => !!x),
      durationDays: c.durationDays,
      startDate: addDays(planStartDate, c.earliestStart), dueDate: addDays(planStartDate, c.earliestFinish),
      earliestStart: c.earliestStart, earliestFinish: c.earliestFinish,
      latestStart: c.latestStart, latestFinish: c.latestFinish, slack: c.slack, critical: c.critical,
    };
  });
}

export type StepState = "done" | "eligible" | "blocked" | "skipped";
export function stepState(step: PlaybookRunStep, byId: Map<string, PlaybookRunStep>): StepState {
  if (step.status === "done") return "done";
  if (step.status === "skipped") return "skipped";
  const unmet = step.dependsOn.some((d) => byId.get(d)?.status !== "done" && byId.get(d)?.status !== "skipped");
  return unmet ? "blocked" : "eligible";
}
